import { useState, useEffect } from "react";
import { 
  FaTrash, FaEye, FaEdit, FaPlus, FaSearch, FaCalendar, FaMoneyBill, 
  FaTicketAlt, FaGift, FaImage, FaTimes, FaUpload, FaChevronLeft, 
  FaChevronRight, FaTag, FaTrophy, FaClock, FaSpinner 
} from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";

const CampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    campaignCategory: "daily",
    price: "",
    noOfTickets: "",
    noOfWinners: "",
    startDate: "",
    endDate: "",
    registrationEndDate: "",
    status: "active",
    images: [],
    prizes: []
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedPrizeImages, setSelectedPrizeImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const campaignsPerPage = 8;
  const apiUrl = "https://api.dreamtoday.in";

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Get all campaigns API with populated category
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/admin/allcampforadmin`);
      
      if (response.data && response.data.success) {
        setCampaigns(response.data.campaigns);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      alert("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  // Delete campaign API
  const handleDelete = async (campaignId) => {
    if (window.confirm("Are you sure you want to delete this campaign?")) {
      try {
        const response = await axios.delete(`${apiUrl}/api/admin/deletecamp/${campaignId}`);
        
        if (response.data && response.data.success) {
          alert("Campaign deleted successfully");
          setCampaigns(campaigns.filter((camp) => camp._id !== campaignId));
        } else {
          alert(response.data.message || "Failed to delete campaign");
        }
      } catch (error) {
        console.error("Error deleting campaign:", error);
        alert(error.response?.data?.message || "Failed to delete campaign");
      }
    }
  };

  // Open edit modal with campaign data
  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
    setEditFormData({
      title: campaign.title || "",
      description: campaign.description || "",
      campaignCategory: campaign.campaignCategory || "daily",
      price: campaign.price || "",
      noOfTickets: campaign.noOfTickets || "",
      noOfWinners: campaign.noOfWinners || "",
      startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().slice(0, 16) : "",
      endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().slice(0, 16) : "",
      registrationEndDate: campaign.registrationEndDate ? new Date(campaign.registrationEndDate).toISOString().slice(0, 16) : "",
      status: campaign.status || "active",
      images: campaign.images || [],
      prizes: campaign.prizes || []
    });
    setIsEditModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle prize changes
  const handlePrizeChange = (index, field, value) => {
    const updatedPrizes = [...editFormData.prizes];
    updatedPrizes[index] = { ...updatedPrizes[index], [field]: value };
    setEditFormData(prev => ({ ...prev, prizes: updatedPrizes }));
  };

  // Add new prize
  const addPrize = () => {
    const isDaily = editFormData.campaignCategory === "daily";
    const newPrize = isDaily 
      ? { 
          prizeName: "", 
          prizeCategory: "cash", 
          description: "", 
          prizeAmount: "", 
          position: editFormData.prizes.length + 1, 
          prizeType: "cash" 
        }
      : { 
          prizeName: "", 
          prizeCategory: "general", 
          description: "", 
          prizeImage: "", 
          position: editFormData.prizes.length + 1, 
          prizeType: "product" 
        };
    
    setEditFormData(prev => ({
      ...prev,
      prizes: [...prev.prizes, newPrize]
    }));
    
    if (!isDaily) {
      setSelectedPrizeImages(prev => [...prev, null]);
    }
  };

  // Remove prize
  const removePrize = (index) => {
    const updatedPrizes = editFormData.prizes.filter((_, i) => i !== index);
    setEditFormData(prev => ({ ...prev, prizes: updatedPrizes }));
    
    if (editFormData.campaignCategory !== "daily") {
      setSelectedPrizeImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  // Handle prize image selection
  const handlePrizeImageSelect = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const updatedSelectedPrizeImages = [...selectedPrizeImages];
      updatedSelectedPrizeImages[index] = file;
      setSelectedPrizeImages(updatedSelectedPrizeImages);
    }
  };

  // Remove selected image
  const removeSelectedImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Remove selected prize image
  const removeSelectedPrizeImage = (index) => {
    setSelectedPrizeImages(prev => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });
  };

  // Edit campaign API
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      
      // Append basic fields
      formData.append("title", editFormData.title);
      formData.append("description", editFormData.description);
      formData.append("campaignCategory", editFormData.campaignCategory);
      formData.append("price", editFormData.price);
      formData.append("noOfTickets", editFormData.noOfTickets);
      formData.append("noOfWinners", editFormData.noOfWinners);
      formData.append("startDate", editFormData.startDate);
      formData.append("endDate", editFormData.endDate);
      formData.append("registrationEndDate", editFormData.registrationEndDate);
      formData.append("status", editFormData.status);
      
      // Append existing images as JSON
      formData.append("existingImages", JSON.stringify(editFormData.images));
      
      // Append new images
      selectedImages.forEach(image => {
        formData.append("images", image);
      });
      
      // Handle prizes based on category
      const isDaily = editFormData.campaignCategory === "daily";
      
      const prizesData = editFormData.prizes.map((prize, index) => {
        const basePrize = {
          prizeName: prize.prizeName,
          prizeCategory: prize.prizeCategory,
          description: prize.description,
          position: index + 1,
          prizeType: isDaily ? "cash" : "product"
        };

        if (isDaily) {
          return {
            ...basePrize,
            prizeAmount: prize.prizeAmount
          };
        } else {
          return {
            ...basePrize,
            prizeImage: prize.prizeImage,
            newImage: selectedPrizeImages[index] ? true : false
          };
        }
      });
      
      formData.append("prizes", JSON.stringify(prizesData));
      
      // Append new prize images for non-daily campaigns
      if (!isDaily) {
        selectedPrizeImages.forEach((image) => {
          if (image) {
            formData.append("prizeImages", image);
          }
        });
      }

      const response = await axios.put(
        `${apiUrl}/api/admin/editcamp/${editingCampaign._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      if (response.data && response.data.success) {
        alert("Campaign updated successfully");
        fetchCampaigns(); // Refresh the list
        setIsEditModalOpen(false);
        setEditingCampaign(null);
        setSelectedImages([]);
        setSelectedPrizeImages([]);
      } else {
        alert(response.data.message || "Failed to update campaign");
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
      alert(error.response?.data?.message || "Failed to update campaign");
    } finally {
      setUploading(false);
    }
  };

  const exportData = (type) => {
    const exportCampaigns = filteredCampaigns.map((camp) => ({
      ID: camp._id,
      Title: camp.title || "N/A",
      Category: camp.campCateId?.name || camp.categoryName || "N/A",
      CategoryID: camp.campCateId?._id || "N/A",
      Description: camp.description?.substring(0, 50) + "..." || "N/A",
      Price: camp.price || 0,
      Tickets: camp.noOfTickets || 0,
      Winners: camp.noOfWinners || 0,
      Status: camp.status || "N/A",
      StartDate: new Date(camp.startDate).toLocaleDateString(),
      EndDate: new Date(camp.endDate).toLocaleDateString(),
      RegistrationEnd: new Date(camp.registrationEndDate).toLocaleDateString(),
      CreatedAt: new Date(camp.createdAt).toLocaleDateString(),
      PrizeCount: camp.prizes?.length || 0
    }));
    
    const ws = utils.json_to_sheet(exportCampaigns);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Campaigns");
    writeFile(wb, `campaigns_${new Date().toISOString().split('T')[0]}.${type}`);
  };

  const openCampaignDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "active":
        return "text-green-600 bg-green-50 border border-green-200";
      case "inactive":
        return "text-gray-600 bg-gray-50 border border-gray-200";
      case "completed":
        return "text-blue-600 bg-blue-50 border border-blue-200";
      default:
        return "text-gray-600 bg-gray-50 border border-gray-200";
    }
  };

  const getCategoryColor = (category) => {
    const categoryStr = typeof category === 'object' ? category?.name : category;
    switch(categoryStr?.toLowerCase()) {
      case "daily":
        return "bg-green-100 text-green-800 border-green-200";
      case "weekly":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "monthly":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getCategoryName = (campaign) => {
    if (campaign.campCateId?.name) {
      return campaign.campCateId.name;
    }
    if (campaign.categoryName) {
      return campaign.categoryName;
    }
    return campaign.campaignCategory || "N/A";
  };

  const filteredCampaigns = campaigns.filter((camp) =>
    (camp.title || "").toLowerCase().includes(search.toLowerCase()) ||
    (camp.description || "").toLowerCase().includes(search.toLowerCase()) ||
    (getCategoryName(camp) || "").toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLastCamp = currentPage * campaignsPerPage;
  const indexOfFirstCamp = indexOfLastCamp - campaignsPerPage;
  const currentCampaigns = filteredCampaigns.slice(indexOfFirstCamp, indexOfLastCamp);
  const totalPages = Math.ceil(filteredCampaigns.length / campaignsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Campaign Management</h1>
        <p className="text-gray-600">Manage all lottery campaigns in the system</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <FaGift className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Campaigns</p>
              <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg mr-4">
              <FaTicketAlt className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Campaigns</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.filter(c => c.status === "active").length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg mr-4">
              <FaMoneyBill className="text-purple-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, camp) => sum + (camp.noOfTickets || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-amber-100 rounded-lg mr-4">
              <FaTrophy className="text-amber-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Prizes</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, camp) => sum + (camp.prizes?.length || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Export */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns by title, description or category..."
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => exportData("csv")}
              className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 font-medium"
            >
              <FaUpload />
              CSV
            </button>
            <button
              onClick={() => exportData("xlsx")}
              className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 font-medium"
            >
              <FaUpload />
              Excel
            </button>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Campaign Details
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Category
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Price & Tickets
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Duration
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Prizes
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentCampaigns.map((campaign) => (
                <tr key={campaign._id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      {campaign.images && campaign.images[0] ? (
                        <img 
                          src={`${apiUrl}${campaign.images[0]}`}
                          alt={campaign.title}
                          className="h-10 w-10 rounded-lg object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/40";
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                          <FaGift className="text-blue-600" />
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {campaign.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {campaign.description?.substring(0, 30)}...
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-6">
                    <div className="flex flex-col">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${getCategoryColor(getCategoryName(campaign))}`}>
                        {getCategoryName(campaign).toUpperCase()}
                      </span>
                      {campaign.campCateId && (
                        <span className="text-xs text-gray-400 mt-1">
                          ID: {campaign.campCateId._id?.slice(-6)}
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm">
                        <FaMoneyBill className="text-gray-400 mr-2" />
                        <span className="font-semibold text-green-600">₹{campaign.price}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <FaTicketAlt className="text-gray-400 mr-2" />
                        <span>{campaign.noOfTickets} tickets</span>
                      </div>
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="text-sm">
                      <div className="font-medium">Start:</div>
                      <div className="text-gray-600">{new Date(campaign.startDate).toLocaleDateString()}</div>
                      <div className="font-medium mt-1">End:</div>
                      <div className="text-gray-600">{new Date(campaign.endDate).toLocaleDateString()}</div>
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="flex -space-x-2">
                      {campaign.prizes?.slice(0, 3).map((prize, idx) => (
                        <div
                          key={idx}
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-white flex items-center justify-center"
                          title={prize.prizeName}
                        >
                          {prize.prizeType === "cash" ? (
                            <FaMoneyBill className="text-green-600 text-xs" />
                          ) : prize.prizeImage ? (
                            <img 
                              src={`${apiUrl}${prize.prizeImage}`}
                              alt={prize.prizeName}
                              className="w-full h-full rounded-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/32";
                              }}
                            />
                          ) : (
                            <FaGift className="text-purple-600 text-xs" />
                          )}
                        </div>
                      ))}
                      {campaign.prizes?.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                          +{campaign.prizes.length - 3}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {campaign.noOfWinners} winners
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(campaign.status)}`}>
                      {campaign.status?.toUpperCase()}
                    </span>
                    {campaign.isRegistrationClosed && (
                      <div className="text-xs text-red-500 mt-1">Registration Closed</div>
                    )}
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openCampaignDetails(campaign)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-blue-200 hover:border-blue-300"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => openEditModal(campaign)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition border border-green-200 hover:border-green-300"
                        title="Edit Campaign"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(campaign._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-200 hover:border-red-300"
                        title="Delete Campaign"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {currentCampaigns.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FaGift className="h-16 w-16 mx-auto" />
            </div>
            <p className="text-gray-500 text-lg font-medium">No campaigns found</p>
            <p className="text-gray-400 mt-2">Try adjusting your search criteria</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredCampaigns.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{indexOfFirstCamp + 1}</span> to{" "}
            <span className="font-semibold">{Math.min(indexOfLastCamp, filteredCampaigns.length)}</span> of{" "}
            <span className="font-semibold">{filteredCampaigns.length}</span> campaigns
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              <FaChevronLeft size={12} /> Previous
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-10 h-10 rounded-lg transition ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              Next <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Campaign Details Modal */}
      {isModalOpen && selectedCampaign && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Sticky Header */}
              <div className="sticky top-0 bg-white z-10 p-6 pb-4 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Campaign Details</h2>
                  <p className="text-gray-600">Complete information about the campaign</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes className="text-gray-500 hover:text-gray-700" size={20} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 pt-2">
                <div className="space-y-6">
                  {/* Campaign Images */}
                  {selectedCampaign.images && selectedCampaign.images.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <FaImage className="mr-2 text-blue-600" /> Campaign Images
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {selectedCampaign.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={`${apiUrl}${image}`}
                              alt={`Campaign ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg shadow-md group-hover:shadow-xl transition"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/150";
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Basic Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-gray-700 mb-3">Basic Information</h3>
                      <div className="space-y-2">
                        <p><span className="text-gray-500">Title:</span> {selectedCampaign.title}</p>
                        <p><span className="text-gray-500">Description:</span> {selectedCampaign.description}</p>
                        <p><span className="text-gray-500">Category:</span> 
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${getCategoryColor(getCategoryName(selectedCampaign))}`}>
                            {getCategoryName(selectedCampaign).toUpperCase()}
                          </span>
                        </p>
                        {selectedCampaign.campCateId && (
                          <p><span className="text-gray-500">Category ID:</span> {selectedCampaign.campCateId._id}</p>
                        )}
                        <p><span className="text-gray-500">Price:</span> <span className="font-semibold text-green-600">₹{selectedCampaign.price}</span></p>
                        <p><span className="text-gray-500">Total Tickets:</span> {selectedCampaign.noOfTickets}</p>
                        <p><span className="text-gray-500">Number of Winners:</span> {selectedCampaign.noOfWinners}</p>
                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-gray-700 mb-3">Dates</h3>
                      <div className="space-y-2">
                        <p><span className="text-gray-500">Start Date:</span> {new Date(selectedCampaign.startDate).toLocaleString()}</p>
                        <p><span className="text-gray-500">End Date:</span> {new Date(selectedCampaign.endDate).toLocaleString()}</p>
                        <p><span className="text-gray-500">Registration Ends:</span> {new Date(selectedCampaign.registrationEndDate).toLocaleString()}</p>
                        <p><span className="text-gray-500">Created:</span> {new Date(selectedCampaign.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Prizes Section */}
                  {selectedCampaign.prizes && selectedCampaign.prizes.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <FaGift className="mr-2 text-purple-600" /> Prizes
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedCampaign.prizes.map((prize, index) => (
                          <div key={prize._id || index} className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition">
                            <div className="flex items-center gap-4">
                              {/* Prize Image/Icon */}
                              <div>
                                {prize.prizeType === "cash" ? (
                                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                                    <FaMoneyBill className="text-green-600 text-3xl" />
                                  </div>
                                ) : prize.prizeImage ? (
                                  <img
                                    src={`${apiUrl}${prize.prizeImage}`}
                                    alt={prize.prizeName}
                                    className="w-16 h-16 rounded-lg object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "https://via.placeholder.com/64";
                                    }}
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <FaGift className="text-purple-600 text-3xl" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Prize Details */}
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">Prize {prize.position || index + 1}</p>
                                <p className="text-gray-700">{prize.prizeName}</p>
                                {prize.prizeType === "cash" ? (
                                  <p className="text-green-600 font-semibold">₹{prize.prizeAmount}</p>
                                ) : (
                                  <p className="text-sm text-gray-500 capitalize">{prize.prizeCategory}</p>
                                )}
                                {prize.description && (
                                  <p className="text-xs text-gray-400 mt-1">{prize.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Winners Section (if any) */}
                  {selectedCampaign.winners && selectedCampaign.winners.length > 0 && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <FaTrophy className="mr-2 text-amber-600" /> Winners
                      </h3>
                      <div className="space-y-3">
                        {selectedCampaign.winners.map((winner) => (
                          <div key={winner._id} className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-gray-900">{winner.userName}</p>
                                <p className="text-sm text-gray-600">{winner.userEmail}</p>
                                <p className="text-sm text-gray-500 mt-1">Ticket: {winner.ticketIds?.join(', ')}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-green-600">
                                  {winner.prizeType === "cash" ? `₹${winner.prizeAmount}` : winner.prizeName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {winner.status === "claimed" ? "✓ Claimed" : "⏳ Pending"}
                                </p>
                                {winner.claimedAt && (
                                  <p className="text-xs text-gray-400">
                                    Claimed: {new Date(winner.claimedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Information */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Status Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Campaign Status</p>
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold mt-1 ${getStatusColor(selectedCampaign.status)}`}>
                          {selectedCampaign.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Registration</p>
                        <p className={`font-semibold mt-1 ${selectedCampaign.isRegistrationClosed ? 'text-red-600' : 'text-green-600'}`}>
                          {selectedCampaign.isRegistrationClosed ? 'Closed' : 'Open'}
                        </p>
                      </div>
                      {selectedCampaign.closedAt && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <p className="text-sm text-gray-500">Closed At</p>
                          <p className="font-medium mt-1">{new Date(selectedCampaign.closedAt).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="sticky bottom-0 bg-white border-t p-6">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      openEditModal(selectedCampaign);
                    }}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                  >
                    Edit Campaign
                  </button>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {isEditModalOpen && editingCampaign && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
            setIsEditModalOpen(false);
            setSelectedImages([]);
            setSelectedPrizeImages([]);
          }}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Sticky Header */}
              <div className="sticky top-0 bg-white z-10 p-6 pb-4 border-b flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Edit Campaign</h2>
                <button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedImages([]);
                    setSelectedPrizeImages([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes className="text-gray-500 hover:text-gray-700" size={20} />
                </button>
              </div>

              {/* Scrollable Form */}
              <div className="p-6 pt-2">
                <form onSubmit={handleUpdate} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Campaign Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={editFormData.title}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Campaign Category *
                      </label>
                      <select
                        name="campaignCategory"
                        value={editFormData.campaignCategory}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                      >
                        <option value="daily">Daily Campaign (Cash Prizes)</option>
                        <option value="weekly">Weekly Campaign (Product Prizes)</option>
                        <option value="monthly">Monthly Campaign (Product Prizes)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (₹) *
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={editFormData.price}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Tickets *
                      </label>
                      <input
                        type="number"
                        name="noOfTickets"
                        value={editFormData.noOfTickets}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Winners *
                      </label>
                      <input
                        type="number"
                        name="noOfWinners"
                        value={editFormData.noOfWinners}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                        min="1"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={editFormData.description}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date *
                      </label>
                      <input
                        type="datetime-local"
                        name="startDate"
                        value={editFormData.startDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date *
                      </label>
                      <input
                        type="datetime-local"
                        name="endDate"
                        value={editFormData.endDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Registration End Date *
                      </label>
                      <input
                        type="datetime-local"
                        name="registrationEndDate"
                        value={editFormData.registrationEndDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        required
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={editFormData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  {/* Campaign Images */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign Images
                    </label>
                    
                    {/* Existing Images */}
                    {editFormData.images.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-2">Existing Images:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {editFormData.images.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={`${apiUrl}${image}`}
                                alt={`Campaign ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/100";
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New Images Upload */}
                    <div className="mt-2">
                      <label className="block text-sm text-gray-600 mb-2">Add New Images:</label>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                      
                      {/* Selected Images Preview */}
                      {selectedImages.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                          {selectedImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={URL.createObjectURL(image)}
                                alt={`New ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={() => removeSelectedImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                              >
                                <FaTimes size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Prizes */}
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Prizes
                      </label>
                      <button
                        type="button"
                        onClick={addPrize}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2"
                      >
                        <FaPlus /> Add Prize
                      </button>
                    </div>

                    <div className="space-y-4">
                      {editFormData.prizes.map((prize, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium">Prize {index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removePrize(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTrash />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">
                                Prize Name
                              </label>
                              <input
                                type="text"
                                value={prize.prizeName}
                                onChange={(e) => handlePrizeChange(index, "prizeName", e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                required
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">
                                Prize Category
                              </label>
                              <select
                                value={prize.prizeCategory}
                                onChange={(e) => handlePrizeChange(index, "prizeCategory", e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              >
                                {editFormData.campaignCategory === "daily" ? (
                                  <>
                                    <option value="cash">Cash</option>
                                    <option value="bonus">Bonus</option>
                                    <option value="jackpot">Jackpot</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="general">General</option>
                                    <option value="electronics">Electronics</option>
                                    <option value="fashion">Fashion</option>
                                    <option value="home">Home & Living</option>
                                    <option value="sports">Sports</option>
                                    <option value="other">Other</option>
                                  </>
                                )}
                              </select>
                            </div>

                            {/* Prize Amount for Daily Campaigns */}
                            {editFormData.campaignCategory === "daily" && (
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                  Prize Amount (₹) *
                                </label>
                                <input
                                  type="number"
                                  value={prize.prizeAmount || ""}
                                  onChange={(e) => handlePrizeChange(index, "prizeAmount", e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                  required
                                  min="0"
                                />
                              </div>
                            )}

                            <div className={editFormData.campaignCategory === "daily" ? "md:col-span-2" : ""}>
                              <label className="block text-sm text-gray-600 mb-1">
                                Description (Optional)
                              </label>
                              <textarea
                                value={prize.description || ""}
                                onChange={(e) => handlePrizeChange(index, "description", e.target.value)}
                                rows="2"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              />
                            </div>
                            
                            {/* Prize Image for Weekly/Monthly Campaigns */}
                            {editFormData.campaignCategory !== "daily" && (
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                  Prize Image
                                </label>
                                
                                {/* Existing Prize Image */}
                                {prize.prizeImage && !selectedPrizeImages[index] && (
                                  <div className="mb-2">
                                    <img
                                      src={`${apiUrl}${prize.prizeImage}`}
                                      alt={prize.prizeName}
                                      className="w-16 h-16 object-cover rounded-lg"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://via.placeholder.com/64";
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* New Prize Image Upload */}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handlePrizeImageSelect(e, index)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                                
                                {/* Selected Prize Image Preview */}
                                {selectedPrizeImages[index] && (
                                  <div className="mt-2 relative inline-block">
                                    <img
                                      src={URL.createObjectURL(selectedPrizeImages[index])}
                                      alt="New prize"
                                      className="w-16 h-16 object-cover rounded-lg"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeSelectedPrizeImage(index)}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                    >
                                      <FaTimes size={12} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Form Buttons */}
                  <div className="flex gap-3 pt-4 sticky bottom-0 bg-white">
                    <button
                      type="submit"
                      disabled={uploading}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Campaign'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditModalOpen(false);
                        setSelectedImages([]);
                        setSelectedPrizeImages([]);
                      }}
                      className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignList;